import { NgForOf, NgIf } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { ActionData, ActionsOperators, AllPrimaryEntitiesNames, BullhornFieldType, CoreEntitiesTypes, Entities, IPromptProfile } from '../../data-rules-model';
import { DataRulesService } from '../../data-rules.service';
import { ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { StaticOptionsProvider } from '../../lib/option-items-providers';
import { IEntityItem, PairedAutocompleteComponent } from '../../lib/paired-autocomplete/paired-autocomplete.component';
import { IActionFieldMeta, IFieldMeta, MetaDataService, MetaSource } from '../../meta-data.service';
import { IrreversibleActionWarningBlockComponent } from '../action-warning-block/irreversible-action-warning-block.component';

@Component({
  selector: 'app-action-ai',
  imports: [
    ReactiveFormsModule,
    PairedAutocompleteComponent,
    MatSelect,
    NgForOf,
    NgIf,
    MatOption,
    IrreversibleActionWarningBlockComponent,
  ],
  templateUrl: './ai-replace.component.html',
  styleUrls: ['./ai-replace.component.scss']
})
export class AiReplaceComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() actionData: ActionData;
  @Input() actionForm: FormGroup;
  @Input() primaryEntity: AllPrimaryEntitiesNames;

  protected readonly CoreEntitiesTypes = CoreEntitiesTypes;
  outputMode: 'FIELD' | 'NOTE';
  readonly noteWarningMessage = 'This action is irreversible and will create new Note entities.';
  destinationFieldControl = new FormControl(null);
  destinationTypeControl = new FormControl(null);
  destinationEntityControl = new FormControl(null);
  actionTypeControl = new FormControl(null);
  aiPromptControl: FormControl;
  noteActionControl: FormControl;
  noteIsDeletedControl: FormControl;
  destinationEntityControlSubscription: Subscription;

  @ViewChild('fieldName')
  private fieldPickerComponent: PairedAutocompleteComponent;

  dataSourceType = MetaSource.ACTION;
  loading = true;

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  prompts: IPromptProfile[] = [];
  entitiesOptions: IEntityItem[];

  constructor(
    public dataRulesService: DataRulesService,
    private metaDataService: MetaDataService
  ) {
  }

  ngOnInit() {
    this.createControls();
    this.getSecondaryEntities();
    this.getAiPrompts();
  }

  ngAfterViewInit() {
    this.getFieldsForSelectedSecondaryEntity(this.actionData.destinationDescriptor);
    this.destinationFieldControl.valueChanges.subscribe(val => this.filterDestinationFieldMeta(val));
    this.destinationEntityControlSubscription = this.destinationEntityControl.valueChanges.subscribe(selectedEntity => {
      this.clearDestinationFieldAndType();
      this.getFieldsForSelectedSecondaryEntity(selectedEntity);
    });
  }

  clearDestinationFieldAndType() {
    this.destinationFieldControl.setValue(null);
    this.destinationTypeControl.setValue(null);
    this.fieldPickerComponent.clearOption();
  }

  // TODO This and all other similar implementations of this need to be moved to the PairedAutoComplete component to remove code duplication
  getFieldsForSelectedSecondaryEntity(selectedEntity: string) {
    this.metaDataService.filterMetaBySecondaryDescriptor(
        this.primaryEntity as Entities, selectedEntity, MetaSource.ACTION
    ).subscribe((meta: IFieldMeta[] | IActionFieldMeta[]) => {
      const options: { name: string; label: string }[] = meta.filter(fm =>
          fm.metaType === BullhornFieldType.STRING || fm.metaType === BullhornFieldType.LONG_STRING
      ).map(fm => (
          { name: fm.fieldName, label: fm.label })
      );
      if (!options) {
        throw new Error(`No meta found for entity: ${ selectedEntity }`);
      }
      const optionsProvider = new StaticOptionsProvider(options);
      if (this.fieldPickerComponent) {
        this.fieldPickerComponent.setOptionsProvider(optionsProvider, this.destinationFieldControl.value);
      }
    });
  }

  getSecondaryEntities() {
    this.metaDataService.reduceMetaToEntitiesOptions(this.primaryEntity as Entities, MetaSource.ACTION).subscribe(entities => {
          this.entitiesOptions = entities;
        }, error => {
          console.error(error);
        }
    );
  }

  getAiPrompts() {
    this.dataRulesService.getAiPrompts(this.primaryEntity).subscribe((prompts) => {
      this.aiPromptControl.enable();
      this.loading = false;
      this.prompts = prompts;
    });
  }

  public promptPlaceholder(): string {
    if (this.loading) {
      return 'Loading prompts...';
    }
    if (this.prompts && this.prompts.length > 0) {
      return 'Choose prompt...';
    }
    return 'You have no prompts.';
  }

  setOutputMode(mode: 'FIELD' | 'NOTE'): void {
    this.outputMode = mode;
    if (mode === 'NOTE') {
      this.actionTypeControl.setValue(ActionsOperators.AIASSIST_NOTE);
      this.destinationFieldControl.setValue(null);
      this.destinationEntityControl.setValue(null);
      this.destinationTypeControl.setValue(null);
      this.destinationFieldControl.clearValidators();
      this.destinationFieldControl.updateValueAndValidity();
    } else {
      this.actionTypeControl.setValue(ActionsOperators.AIASSIST);
      this.destinationFieldControl.setValidators(Validators.required);
      this.destinationFieldControl.updateValueAndValidity();
      setTimeout(() => this.getFieldsForSelectedSecondaryEntity(this.destinationEntityControl.value));
    }
  }

  private createControls(): void {
    this.outputMode = this.actionData.type === ActionsOperators.AIASSIST_NOTE ? 'NOTE' : 'FIELD';

    this.destinationFieldControl = new FormControl(this.actionData.destination);
    if (this.outputMode === 'FIELD') {
      this.destinationFieldControl.setValidators(Validators.required);
    }
    this.destinationEntityControl = new FormControl(this.actionData.destinationDescriptor);
    this.destinationTypeControl = new FormControl(this.actionData.destinationType);
    this.actionTypeControl = new FormControl(this.actionData.type);
    this.aiPromptControl = new FormControl({ value: this.actionData.value, disabled: true });
    this.noteActionControl = new FormControl(
      this.actionData.noteAction ?? 'Kyloe AI Assist Result',
      [Validators.required, Validators.maxLength(30)]
    );
    this.noteIsDeletedControl = new FormControl(this.actionData.noteIsDeleted ?? false);

    this.actionForm.addControl('destination', this.destinationFieldControl);
    this.actionForm.addControl('destinationDescriptor', this.destinationEntityControl);
    this.actionForm.addControl('destinationType', this.destinationTypeControl);
    this.actionForm.addControl('type', this.actionTypeControl);
    this.actionForm.addControl('value', this.aiPromptControl);
    this.actionForm.addControl('noteAction', this.noteActionControl);
    this.actionForm.addControl('noteIsDeleted', this.noteIsDeletedControl);

    const typeValue = this.outputMode === 'NOTE'
      ? ActionsOperators.AIASSIST_NOTE
      : ActionsOperators.AIASSIST;
    this.actionTypeControl.setValue(typeValue);
    this.aiPromptControl.setValue(this.actionData.value);
  }

  private filterDestinationFieldMeta(search: string) {
    this.metaDataService.getActionMeta(this.primaryEntity as Entities).subscribe(fm => {
      const meta = fm.find(m => m.fieldName === search);
      this.destinationTypeControl.setValue(meta ? meta.metaType : null);
    });
  }

  removeAllControls() {
    this.actionForm.removeControl('destination');
    this.actionForm.removeControl('destinationDescriptor');
    this.actionForm.removeControl('destinationType');
    this.actionForm.removeControl('type');
    this.actionForm.removeControl('value');
    this.actionForm.removeControl('noteAction');
    this.actionForm.removeControl('noteIsDeleted');
  }

  ngOnDestroy(): void {
    this.destinationEntityControlSubscription.unsubscribe();
    this.removeAllControls();
  }

}
